/**
 * `getAuthorSession` — read ONCE by the cabinet shell, not once per screen.
 *
 * In a LIVE build the shell is the route, `routes/support/session-routes.tsx`, which puts the
 * session `AuthSessionProvider` already holds on the context below. So this hook does two jobs:
 *
 *   - If the shell has put a session on `CabinetSessionContext`, USE IT AND ISSUE NOTHING. `GET
 *     /v1/me` is "one read renders the app"; a support page that fetched it again would be a second
 *     read of the same fact on every navigation.
 *   - If nothing has, fetch it here so this feature is complete on its own today.
 *
 * The default build takes the second branch - its fixture answers `GET /v1/me` - exactly as it did
 * before the route bound the first; this file did not have to move for either.
 *
 * WHAT IS READ FROM IT: `termsAcceptanceRequired` — "True when the live terms version has not been
 * accepted. It blocks submitting, resubmitting and publishing — and never a payout."
 */
import { createContext, useCallback, useContext } from 'react';
import type { AuthorSession } from '../api/contract';
import { getAuthorSession } from '../api/operations';
import { useApiTransport } from '../api/transport';
import { useAsyncResource } from './use-async-resource';
import type { AsyncResource } from './use-async-resource';

/** `null` means "no shell has supplied one" — not "there is no session". */
export const CabinetSessionContext = createContext<AuthorSession | null>(null);

const NOOP = () => {
  // A session handed down by the shell is the shell's to reload.
};

export function useCabinetSession(): AsyncResource<AuthorSession> {
  const provided = useContext(CabinetSessionContext);
  const transport = useApiTransport();

  const read = useCallback(
    (signal: AbortSignal) => getAuthorSession(transport, { signal }),
    [transport],
  );

  // Called unconditionally so the hook order never changes; `enabled` is what suppresses the read.
  const fetched = useAsyncResource(read, provided === null);

  if (provided !== null) {
    return { status: 'ready', data: provided, error: null, reloading: false, reload: NOOP };
  }
  return fetched;
}
